// Canvas-painted glyphs shared by the toolkit and the radial menu. Each
// function takes a 2D context plus a (cx, cy) center and a `size` in
// canvas pixels, paints the glyph centered on (cx, cy), and restores the
// context state. Pure — no app state read or mutated.
//
// `withCenteredCanvas` handles the per-icon setup/teardown (save,
// translate to center, normalize the 256-px design scale, default white
// stroke + fill, round line joins/caps) so each icon body is just the
// shape data + per-shape overrides.

type IconDraw = (ctx: CanvasRenderingContext2D, s: number) => void;

function withCenteredCanvas(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
  draw: IconDraw,
): void {
  ctx.save();
  ctx.translate(cx, cy);
  const s = size / 256;
  ctx.strokeStyle = "#ffffff";
  ctx.fillStyle = "#ffffff";
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  draw(ctx, s);
  ctx.restore();
}

// Filled triangle "arrowhead" at the tip of a line segment. `(tx, ty)` is
// the tip; `(dx, dy)` is the line's direction (unit vector). The two
// back vertices sit `head` behind the tip and `head * perpScale` to either
// side perpendicular to the direction.
// Isometric cube corners — a diamond at +y/-y (top/bottom) plus a square
// of vertical edges. Returns the top-face rhombus and bottom-face rhombus
// as four-vertex arrays so callers can stroke / fill / decorate them.
function isoCubeCorners(
  w: number,
  h: number,
  depth: number,
): { top: [number, number][]; bot: [number, number][] } {
  return {
    top: [
      [0, -h - depth],
      [w, -depth],
      [0, h - depth],
      [-w, -depth],
    ],
    bot: [
      [0, -h],
      [w, 0],
      [0, h],
      [-w, 0],
    ],
  };
}

function strokeQuad(
  ctx: CanvasRenderingContext2D,
  pts: [number, number][],
): void {
  ctx.beginPath();
  ctx.moveTo(pts[0][0], pts[0][1]);
  for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
  ctx.closePath();
  ctx.stroke();
}

function fillQuad(
  ctx: CanvasRenderingContext2D,
  pts: [number, number][],
): void {
  ctx.beginPath();
  ctx.moveTo(pts[0][0], pts[0][1]);
  for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
  ctx.closePath();
  ctx.fill();
}

export function paintPencilIcon(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
): void {
  withCenteredCanvas(ctx, cx, cy, size, (ctx, s) => {
    ctx.rotate(Math.PI / 4); // pencil drawn on the diagonal
    const halfLen = 95 * s;
    const halfW = 26 * s;
    const tipLen = 40 * s;
    ctx.lineWidth = 12 * s;
    // Body — eraser end toward -y, sharpened end toward +y.
    ctx.beginPath();
    ctx.moveTo(-halfW, -halfLen);
    ctx.lineTo(halfW, -halfLen);
    ctx.lineTo(halfW, halfLen - tipLen);
    ctx.lineTo(-halfW, halfLen - tipLen);
    ctx.closePath();
    ctx.stroke();
    // Sharpened point.
    ctx.beginPath();
    ctx.moveTo(-halfW, halfLen - tipLen);
    ctx.lineTo(halfW, halfLen - tipLen);
    ctx.lineTo(0, halfLen);
    ctx.closePath();
    ctx.fill();
    // Ferrule band near the eraser end.
    ctx.lineWidth = 10 * s;
    ctx.beginPath();
    ctx.moveTo(-halfW, -halfLen + 34 * s);
    ctx.lineTo(halfW, -halfLen + 34 * s);
    ctx.stroke();
  });
}

// A cube in isometric-ish projection with an arrow pulling its top face
// up — reads as "drag this face to extrude the box".
export function paintExtrudeIcon(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
): void {
  withCenteredCanvas(ctx, cx, cy, size, (ctx, s) => {
    ctx.lineWidth = 10 * s;
    const w = 60 * s;
    const h = 36 * s;
    const depth = 50 * s;
    const { top, bot } = isoCubeCorners(w, h, depth);
    // Top face — light fill + stroked outline.
    ctx.globalAlpha = 0.35;
    fillQuad(ctx, top);
    ctx.globalAlpha = 1;
    strokeQuad(ctx, top);
    // Front three vertical edges + the two front bottom edges.
    ctx.beginPath();
    ctx.moveTo(top[2][0], top[2][1]);
    ctx.lineTo(bot[2][0], bot[2][1]);
    ctx.moveTo(top[1][0], top[1][1]);
    ctx.lineTo(bot[1][0], bot[1][1]);
    ctx.moveTo(top[3][0], top[3][1]);
    ctx.lineTo(bot[3][0], bot[3][1]);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(bot[3][0], bot[3][1]);
    ctx.lineTo(bot[2][0], bot[2][1]);
    ctx.lineTo(bot[1][0], bot[1][1]);
    ctx.stroke();
    // Up-arrow above the top vertex.
    const arrowBase = top[0][1] - 6 * s;
    const arrowTip = arrowBase - 70 * s;
    const arrowHalf = 22 * s;
    ctx.lineWidth = 14 * s;
    ctx.beginPath();
    ctx.moveTo(0, arrowBase);
    ctx.lineTo(0, arrowTip + 12 * s);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(0, arrowTip);
    ctx.lineTo(-arrowHalf, arrowTip + arrowHalf);
    ctx.lineTo(arrowHalf, arrowTip + arrowHalf);
    ctx.closePath();
    ctx.fill();
  });
}

// Solid isometric cube — one voxel, distinguished from EXTRUDE by
// EXTRUDE's up-arrow over its same cube outline. Three faces shaded
// differently (top brightest, left darkest) to read as 3D.
export function paintVoxelIcon(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
): void {
  withCenteredCanvas(ctx, cx, cy, size, (ctx, s) => {
    ctx.lineWidth = 8 * s;
    const { top, bot } = isoCubeCorners(70 * s, 40 * s, 60 * s);
    // Top cap (lightest).
    ctx.fillStyle = "rgba(255,255,255,0.95)";
    fillQuad(ctx, top);
    strokeQuad(ctx, top);
    // Right face (medium).
    ctx.fillStyle = "rgba(255,255,255,0.55)";
    fillQuad(ctx, [top[1], top[2], bot[2], bot[1]]);
    strokeQuad(ctx, [top[1], top[2], bot[2], bot[1]]);
    // Left face (darkest).
    ctx.fillStyle = "rgba(255,255,255,0.3)";
    fillQuad(ctx, [top[2], top[3], bot[3], bot[2]]);
    strokeQuad(ctx, [top[2], top[3], bot[3], bot[2]]);
  });
}

// Rectangular eraser on a diagonal — mirrors the pencil silhouette for
// DRAW so the two action buttons read as a pair.
export function paintEraseIcon(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
): void {
  withCenteredCanvas(ctx, cx, cy, size, (ctx, s) => {
    ctx.rotate(Math.PI / 4);
    const halfLen = 95 * s;
    const halfW = 36 * s;
    ctx.lineWidth = 12 * s;
    // Eraser body.
    ctx.beginPath();
    ctx.moveTo(-halfW, -halfLen);
    ctx.lineTo(halfW, -halfLen);
    ctx.lineTo(halfW, halfLen);
    ctx.lineTo(-halfW, halfLen);
    ctx.closePath();
    ctx.stroke();
    // Divider band a third of the way down — the "metal ferrule" /
    // colour split that gives an eraser its silhouette.
    ctx.lineWidth = 10 * s;
    ctx.beginPath();
    ctx.moveTo(-halfW, -halfLen + 60 * s);
    ctx.lineTo(halfW, -halfLen + 60 * s);
    ctx.stroke();
  });
}

// Four-directional arrows in a cross — MOVE action.
export function paintMoveIcon(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
): void {
  withCenteredCanvas(ctx, cx, cy, size, (ctx, s) => {
    ctx.lineWidth = 12 * s;
    const arm = 80 * s;
    const head = 30 * s;
    // Cross spine.
    ctx.beginPath();
    ctx.moveTo(-arm, 0);
    ctx.lineTo(arm, 0);
    ctx.moveTo(0, -arm);
    ctx.lineTo(0, arm);
    ctx.stroke();
    // Arrowheads at the four tips.
    const tips: [number, number, number, number][] = [
      [arm, 0, -1, 0], // right
      [-arm, 0, 1, 0], // left
      [0, arm, 0, -1], // down
      [0, -arm, 0, 1], // up
    ];
    for (const [tx, ty, bx, by] of tips) {
      const px = -by;
      const py = bx;
      ctx.beginPath();
      ctx.moveTo(tx, ty);
      ctx.lineTo(tx + bx * head + px * head * 0.6, ty + by * head + py * head * 0.6);
      ctx.lineTo(tx + bx * head - px * head * 0.6, ty + by * head - py * head * 0.6);
      ctx.closePath();
      ctx.fill();
    }
  });
}

// Trash can — DELETE action.
export function paintDeleteIcon(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
): void {
  withCenteredCanvas(ctx, cx, cy, size, (ctx, s) => {
    ctx.lineWidth = 12 * s;
    const halfW = 70 * s;
    const top = -70 * s;
    const bot = 90 * s;
    const lidTop = -90 * s;
    // Body (trapezoid: slightly narrower at base).
    const bodyW = halfW * 0.86;
    ctx.beginPath();
    ctx.moveTo(-halfW, top);
    ctx.lineTo(halfW, top);
    ctx.lineTo(bodyW, bot);
    ctx.lineTo(-bodyW, bot);
    ctx.closePath();
    ctx.stroke();
    // Lid line.
    ctx.beginPath();
    ctx.moveTo(-halfW - 14 * s, top);
    ctx.lineTo(halfW + 14 * s, top);
    ctx.stroke();
    // Handle tab on the lid.
    ctx.beginPath();
    ctx.moveTo(-28 * s, top);
    ctx.lineTo(-22 * s, lidTop);
    ctx.lineTo(22 * s, lidTop);
    ctx.lineTo(28 * s, top);
    ctx.stroke();
    // Three vertical hash marks inside the body.
    ctx.lineWidth = 8 * s;
    const inset = 24 * s;
    for (const x of [-inset, 0, inset]) {
      ctx.beginPath();
      ctx.moveTo(x, top + 22 * s);
      ctx.lineTo(x * 0.86, bot - 18 * s);
      ctx.stroke();
    }
  });
}

// Gear / cog — the SETTINGS utility glyph. Flat-topped teeth around a
// stroked body with a center hole.
export function paintGearIcon(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
): void {
  withCenteredCanvas(ctx, cx, cy, size, (ctx, s) => {
    ctx.lineWidth = 13 * s;
    const teeth = 8;
    const step = (Math.PI * 2) / teeth;
    const tw = step * 0.26; // half angular width of a tooth top
    const rO = 96 * s; // tooth tip
    const rI = 66 * s; // valley / body
    ctx.beginPath();
    let first = true;
    for (let i = 0; i < teeth; i++) {
      const b = i * step;
      const pts: [number, number][] = [
        [b - step / 2, rI],
        [b - tw, rO],
        [b + tw, rO],
        [b + step / 2, rI],
      ];
      for (const [a, r] of pts) {
        const x = Math.cos(a) * r;
        const y = Math.sin(a) * r;
        if (first) {
          ctx.moveTo(x, y);
          first = false;
        } else {
          ctx.lineTo(x, y);
        }
      }
    }
    ctx.closePath();
    ctx.stroke();
    // Center hole.
    ctx.beginPath();
    ctx.arc(0, 0, 30 * s, 0, Math.PI * 2);
    ctx.stroke();
  });
}

// Iso outline of an empty box — the BOX shape mode. Same isometric cage
// as the EXTRUDE icon, minus the arrow.
// Flat disc drawn in isometric (a filled ellipse) — the CIRCLE shape lays a disc
// on the surface you drag across, so a foreshortened circle reads it best.
export function paintCircleIcon(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
): void {
  withCenteredCanvas(ctx, cx, cy, size, (ctx, s) => {
    ctx.lineWidth = 10 * s;
    ctx.beginPath();
    ctx.ellipse(0, 0, 80 * s, 50 * s, 0, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(255,255,255,0.35)";
    ctx.fill();
    ctx.stroke();
  });
}

export function paintBoxIcon(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
): void {
  withCenteredCanvas(ctx, cx, cy, size, (ctx, s) => {
    ctx.lineWidth = 10 * s;
    const { top, bot } = isoCubeCorners(70 * s, 40 * s, 60 * s);
    // Top rhombus.
    strokeQuad(ctx, top);
    // Three visible verticals from top vertices down to bottom rhombus.
    ctx.beginPath();
    ctx.moveTo(top[1][0], top[1][1]);
    ctx.lineTo(bot[1][0], bot[1][1]);
    ctx.moveTo(top[2][0], top[2][1]);
    ctx.lineTo(bot[2][0], bot[2][1]);
    ctx.moveTo(top[3][0], top[3][1]);
    ctx.lineTo(bot[3][0], bot[3][1]);
    ctx.stroke();
    // Two front bottom edges.
    ctx.beginPath();
    ctx.moveTo(bot[3][0], bot[3][1]);
    ctx.lineTo(bot[2][0], bot[2][1]);
    ctx.lineTo(bot[1][0], bot[1][1]);
    ctx.stroke();
  });
}
